import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Provider,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { GuidEmpty, MbsSize, ModalService, ModalSettings, TableCellDirective, TableHeader } from 'mbs-ui-kit';
import { TreeBunchesIcons, TreeRestorePointsIconPath } from 'mbs-ui-shared@models/backup/storages-type';
import { PlanMode, PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { isCBFBunch } from 'mbs-ui-shared@utils/is-cbf-bunch';
import { noop } from 'rxjs';
import {
  ArchiveType,
  ArchiveTypeStrings,
  BackupToRestoreStepValue,
  BaseArchiveIcons,
  BunchItem,
  cbfMap,
  IdsForCurrentFormats
} from 'wizards/models/backup-to-restore-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';

import { AlertComponent, ListSelectComponent, TagComponent } from 'mbs-ui-kit';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';
import { StepBase } from 'wizards/steps/StepBase.class';

const BackupToRestoreStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => BackupToRestoreStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-backup-to-restore-step',
  templateUrl: './backup-to-restore-step.component.html',
  providers: [BackupToRestoreStepValueAccessor],
  imports: [AlertComponent, CommonModule, I18NextPipe, ListSelectComponent, ReactiveFormsModule, TagComponent, TableCellDirective]
})
export class BackupToRestoreStepComponent extends StepBase<BackupToRestoreStepValue> implements OnInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);
  i18nextPipe = inject(I18NextPipe);
  mainService: RemoteManagementWizardsService;
  private modalService = inject(ModalService);
  private ngZone = inject(NgZone);
  private stepService = inject(WizardStepsService);

  @ViewChild('nothingRestore', { static: true, read: TemplateRef }) nothingRestore: TemplateRef<HTMLElement>;

  @Input() storageId: string;
  @Input() computerName: string;
  public readonly MbsSize = MbsSize;
  public readonly cbfMap = cbfMap;
  public readonly iconMap = {
    [PlanTypes.RestorePlan]: BaseArchiveIcons.Files,
    [PlanTypes.RestoreDiskImagePlan]: BaseArchiveIcons.DiskImage,
    [PlanTypes.RestoreHyperV]: TreeBunchesIcons.HyperV,
    [PlanTypes.RestoreVMware]: TreeBunchesIcons.VmWare,
    [PlanTypes.RestoreHyperVToCustom]: TreeBunchesIcons.HyperV,
    [PlanTypes.RestoreVMwareToCustom]: TreeBunchesIcons.VmWare,
    [PlanTypes.RestoreDataBasePlan]: TreeRestorePointsIconPath.DBPlan,
    [PlanTypes.RestoreDatabaseFilesPlan]: TreeRestorePointsIconPath.DBPlan,
    [PlanTypes.RestoreProxmox]: TreeBunchesIcons.Proxmox
  };
  public readonly typeDisplay = {
    [PlanTypes.RestoreDiskImagePlan]: 'Image-Based',
    [PlanTypes.RestorePlan]: 'Files',
    [PlanTypes.RestoreVMware]: 'VMware',
    [PlanTypes.RestoreVMwareToCustom]: 'VMware',
    [PlanTypes.RestoreHyperV]: 'Hyper-V',
    [PlanTypes.RestoreHyperVToCustom]: 'Hyper-V',
    [PlanTypes.RestoreDataBasePlan]: 'SQL',
    [PlanTypes.RestoreDatabaseFilesPlan]: 'SQL',
    [PlanTypes.RestoreProxmox]: 'Proxmox'
  };
  private readonly noDataModalSettings: ModalSettings = {
    header: { title: this.i18nextPipe.transform('wizards:noDataToRestore') },
    footer: { okButton: { show: false }, cancelButton: { text: this.i18nextPipe.transform('shared:buttons.close') } }
  };
  private readonly archiveTypes = {
    [PlanTypes.RestoreProxmox]: ArchiveType.Proxmox,
    [PlanTypes.RestorePlan]: ArchiveType.Files,
    [PlanTypes.RestoreDiskImagePlan]: ArchiveType.DiskImage,
    [PlanTypes.RestoreHyperV]: ArchiveType.HyperV,
    [PlanTypes.RestoreVMware]: ArchiveType.VMware,
    [PlanTypes.RestoreHyperVToCustom]: ArchiveType.HyperV,
    [PlanTypes.RestoreVMwareToCustom]: ArchiveType.VMware,
    [PlanTypes.RestoreDataBasePlan]: ArchiveType.MsSql,
    [PlanTypes.RestoreDatabaseFilesPlan]: ArchiveType.MsSql
  };

  public headers: TableHeader[] = [{ name: '', gridColSize: '10fr', class: '-stretch' }];
  public bunches: BunchItem[] = [];

  get previewMode(): boolean {
    const form = this.mainService.initialFormData;
    return !this.isEdit && this.isRestoreIbb && form?.backupToRestoreStep && form.whereBackupStep?.ConnectionID === this.storageId;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.stepForm = new UntypedFormGroup({
      selectedBunch: new FormControl('', [Validators.required]),
      backupPlanName: new FormControl('')
    });

    this.initFormEvents();
  }

  onStepFormChange(value: BackupToRestoreStepValue): void {
    if (this.stepForm.dirty || this.stepForm.touched) {
      this.value = { ...value, valid: this.stepForm.valid };
      this.stepService.backupPlanName = value?.backupPlanName;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.stepForm &&
      ((changes.computerName && !changes.computerName.currentValue) ||
        (changes.storageId && (!changes.storageId.currentValue || changes.storageId.currentValue === GuidEmpty || !this.computerName)))
    ) {
      this.resetValues();
    }

    if (changes.computerName && changes.computerName.currentValue && this.storageId && this.storageId !== GuidEmpty) {
      if (this.previewMode) this.setSelectedBunch();
      else this.getBunches();
    }
  }

  setBunches(bunches: BunchItem[], type: ArchiveType): void {
    const isFiles = type === ArchiveType.Files;
    const archiveType = ArchiveType[type];

    this.bunches = bunches
      .map((bunch: BunchItem) => {
        if (!isNil(bunch.type)) return bunch;

        return bunch.displayName === ArchiveTypeStrings[archiveType] || (isFiles && bunch.displayName === IdsForCurrentFormats.FileUnix)
          ? { ...bunch, type: archiveType }
          : bunch;
      })
      .filter((bunch: BunchItem) => bunch.type === archiveType)
      .map((bunch: BunchItem) => {
        if (!bunch.bunchName) bunch.bunchName = bunch.displayName;
        if (!bunch.bunchId) bunch.bunchId = bunch.bunchName;
        if (bunch.bunchId === IdsForCurrentFormats.Ibb) bunch.bunchName = bunch.bunchName.replace(' ', '-');

        return bunch;
      })
      .sort((a: BunchItem) => (isCBFBunch(a.bunchId) ? 1 : -1));

    if (!this.bunches?.length) this.showInfoModal();
  }

  showInfoModal(): void {
    this.modalService.open(this.noDataModalSettings, this.nothingRestore).finally(noop);
  }

  updateSelectedBunchFromName(): void {
    const backupPlanName = this.stepForm.get('backupPlanName').value;
    const found =
      this.bunches.length === 1
        ? this.bunches[0]
        : this.bunches.find((b: BunchItem) => b.bunchName === backupPlanName || b.bunchId === backupPlanName);

    this.stepForm.get('selectedBunch').markAsTouched();
    this.stepForm.get('selectedBunch').setValue('');

    queueMicrotask(() => {
      this.ngZone.run(() => {
        if (found) {
          this.stepForm.get('selectedBunch').setValue(found.bunchId);
          this.mainService.isNBF = !isCBFBunch(found.bunchId);
        } else if (this.mainService.mode === PlanMode.create) {
          const currentFound = this.bunches.find((c: BunchItem) => isCBFBunch(c.bunchId));

          if (currentFound) this.stepForm.get('selectedBunch').setValue(currentFound.bunchId);

          this.mainService.isNBF = false;
        }

        this.loadInfo.emit({ loading: false });
        this.cdr.detectChanges();
      });
    });
  }

  clickHandler(): void {
    if (this.stepForm.dirty) return;

    this.stepForm.markAsDirty();
    this.stepForm.updateValueAndValidity();
  }

  changeSelectedHandler(selectedBunches: string[]): void {
    if (!selectedBunches?.length) {
      this.stepForm.get('selectedBunch').setValue('');
      return void this.cdr.detectChanges();
    }

    const selectedId = selectedBunches[0];
    this.stepForm.get('selectedBunch').setValue(selectedId);
    const bunch = this.bunches.find((b: BunchItem) => b.bunchId === selectedId);

    if (bunch) this.stepForm.get('backupPlanName').setValue(bunch.bunchName);

    this.mainService.isNBF = !isCBFBunch(selectedId);
    this.cdr.detectChanges();
  }

  // Private block
  private resetValues(): void {
    this.bunches = [];
    this.stepForm.get('selectedBunch').setValue('');
    this.stepForm.get('backupPlanName').setValue('');
  }

  private setSelectedBunch(): void {
    this.loadInfo.emit({ loading: true });
    setTimeout(() => {
      const bunchId = this.mainService.initialFormData.backupToRestoreStep.bunchId;
      const bunchName = this.mainService.initialFormData.backupToRestoreStep.backupPlanName;
      this.bunches = [{ bunchId, bunchName, disabled: true } as BunchItem];
      this.updateSelectedBunchFromName();
    }, 16);
  }

  private getBunches(): void {
    this.bunches = [];
    this.stepForm.get('selectedBunch').setValue('');
    const treeParams = {
      agentType: 'backup',
      commandType: 'GetBackupContent',
      params: {
        SessionId: null,
        ConnectionId: null,
        Password: '',
        ContentFilter: 'Actual',
        path: `${this.storageId}${this.joinForInput}${this.computerName}`,
        offset: null,
        limit: null,
        order: 'TypeDesc'
      }
    };

    if (this.mainService.sessionId) treeParams.params.SessionId = this.mainService.sessionId;

    this.loadInfo.emit({ loading: true });

    this.stepService
      .getRemoteCommandData(treeParams, this.mainService.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (bunches) => {
          if (!bunches?.data?.items?.length) return void this.loadInfo.emit({ loading: false });

          this.setBunches(bunches.data.items, this.archiveTypes[this.planType]);
          this.updateSelectedBunchFromName();
        },
        error: () => {
          this.loadInfo.emit({ loading: false });
          this.showInfoModal();
          this.stepForm.get('selectedBunch').setValue('');
          this.stepForm.get('backupPlanName').setValue('');
        }
      });
  }
}
